// The app's view of a worldview, adapted from the API's wire shapes (src/api/adapt.ts). Following PRD §7.
// Dates are "YYYY-MM" (end of month). Ids are node handles, link ids and scenario ids from the API.

export type Month = string;

export type Source = "manual" | "market" | "crowd" | "feed" | "formula";
export type Visibility = "public" | "private";

export type Op = "<" | "<=" | ">" | ">=";

export type Condition = { event: string; for_months?: number } | { metric: string; op: Op; value: number; for_months?: number };

export interface Definition {
	combine: "all" | "any";
	when: Condition[];
	at?: string; // evaluate only in the month this event happens
}

interface NodeBase {
	id: string; // the node's handle: its formula name and the id in URLs and API paths
	title: string;
	/** Font Awesome icon name (see lib/icons.ts). */
	icon: string;
	question: string;
	resolution: { criteria: string; source: string };
	key_dates: Month[];
	hero_date?: Month;
	/** The date the headline numbers are at: `hero_date`, or the API's default for the node. */
	headline_date: Month;
	visibility: Visibility;
	tags: string[];
	source: Source;
	note?: string;
	/** Last month this node is forecast to. */
	horizon: Month;
	/** The forecast's version, sent as `If-Match` on the next save. */
	version: number;
	/** Saved by an AI (MCP) and not yet saved by a person in the app. */
	aiDraft: boolean;
	pos?: { x: number; y: number };
	/** Nodes its formula or definition uses (from `GET /worldview`: the API parses formulas, the app never does). */
	inputs: string[];
}

/** One rung of a prediction market's date ladder ("by this date?"). */
export interface MarketRung {
	date: string; // YYYY-MM-DD
	p: number; // mid price as a probability
	volume: number; // USD traded on this rung
	resolved?: boolean; // settled "no": it hadn't happened by this date
}

export interface MarketLadder {
	venue: string;
	title: string;
	url?: string;
	as_of: string; // YYYY-MM-DD
	rungs: MarketRung[];
}

export interface EventNode extends NodeBase {
	kind: "event";
	/** Baseline "chance by date", before drivers adjust it. */
	by: Record<Month, number>;
	definition?: Definition;
	happened?: Month | null; // resolution: month it happened
	market?: MarketLadder;
}

export interface MetricNode extends NodeBase {
	kind: "metric";
	unit: string; // "USD" | "people" | "%" | "x" | ...
	scale: "growth" | "level";
	range: Record<Month, [number, number, number]>; // low / likely / high
	formula?: string;
	observed: { month: Month; value: number }[]; // history from feed / import / logged
}

export type ModelNode = EventNode | MetricNode;

export type Link =
	| {
			id: string;
			type: "trigger";
			from: string;
			to: string;
			p: number;
			within_months: number;
			lag_months: number;
	  }
	| {
			id: string;
			type: "multiplier";
			from: string;
			to: string;
			x: number;
			for_months?: number | null;
	  }
	| {
			id: string;
			type: "multiplier";
			from: { metric: string };
			to: string;
			/** A ladder of thresholds: each step applies from its value up to the next step's. The first has no `from`. */
			steps: Step[];
	  }
	| {
			id: string;
			type: "impact";
			from: string;
			to: string;
			change: number;
			over_months: number;
			recovery_months: number | null;
	  };

/** One step of a metric-driven multiplier ladder. */
export interface Step {
	from?: number;
	x: number;
}

export type LeverMode = "assume" | "make";

export type Lever =
	| {
			id: string;
			kind: "event";
			node: string;
			happens: "at" | "by" | "true" | "false";
			month?: Month;
			mode: LeverMode;
	  }
	| {
			/** The scenario's own "chance by date" curve for an event, edited like the baseline. */
			id: string;
			kind: "curve";
			node: string;
			by: Record<Month, number>;
	  }
	| {
			/** The scenario's own low / likely / high range for a metric, edited like the baseline. */
			id: string;
			kind: "range";
			node: string;
			range: Record<Month, [number, number, number]>;
	  };

/** A named what-if across the whole worldview: per-node values or outcomes that replace the baseline. */
export interface Scenario {
	id: string;
	slug: string;
	name: string;
	description?: string;
	levers: Lever[];
}

export interface Worldview {
	nodes: Record<string, ModelNode>;
	links: Link[];
	scenarios: Scenario[];
}

/** The node a link comes from. */
export function linkFrom(l: Link): string {
	return typeof l.from === "string" ? l.from : l.from.metric;
}
